'use client';

import { InputChips } from '@/_components';
import type { QuestionFormInputChipsProps } from './QuestionFormInputChips.types';
import {
  useContext,
  useState,
  useMemo,
  type FC,
  useCallback,
  useEffect,
} from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import {
  QUESTION_SEPARATOR,
  SAVE_TOGGLE_DELAY_MS,
} from '@/_constants/questions';
import styles from './QuestionFormInputChips.module.css';

export const QuestionFormInputChips: FC<QuestionFormInputChipsProps> = ({
  question,
  isDisabled,
  onChange,
  mode,
}) => {
  const { updateQuestion } = useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [answerToDebounce, setAnswerToDebounce] = useState<
    | {
        questionId: number;
        answer: string;
      }
    | undefined
  >(undefined);

  const debouncedAnswer = useDebounce(answerToDebounce, SAVE_TOGGLE_DELAY_MS);

  const answerQuestion = useCallback(
    async (answerParams: { questionId: number; answer: string }) => {
      if (mode === 'admin-preview') {
        await updatePreviewQuestion({ ...answerParams });
      } else if (mode === 'admin-template') {
        await updateTemplateQuestion(answerParams);
      } else {
        await updateQuestion({ ...answerParams, isAsync: true });
      }

      return setLoading(false);
    },
    [mode, updateQuestion, updateTemplateQuestion, updatePreviewQuestion],
  );

  useEffect(() => {
    if (debouncedAnswer) {
      answerQuestion(debouncedAnswer);
    }
  }, [debouncedAnswer]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAnswer = async (selectedChips: Set<string>) => {
    const answerArray = Array.from(selectedChips);
    const answer = answerArray.join(QUESTION_SEPARATOR);
    const answerParams = { questionId: question.id, answer };

    setLoading(true);

    onChange?.(answer);
    setAnswerToDebounce(answerParams);
  };

  const inputChipsItems = useMemo(() => {
    return (
      question.options?.map((option: string) => ({
        name: option,
        value: option,
      })) || []
    );
  }, [question.options]);

  const [chips, setChips] = useState<Set<string>>(new Set<string>());

  // Update chips when question.answer changes
  useEffect(() => {
    if (!question.answer) {
      setChips(new Set<string>());
    } else {
      const answerArray = question.answer
        .split(QUESTION_SEPARATOR)
        .filter(Boolean);
      setChips(new Set(answerArray));
    }
  }, [question.answer]);

  const [inputValue, setInputValue] = useState<string>('');

  const handleChipSelect = (newChips: Set<string>) => {
    setChips(newChips);
    onAnswer(newChips);
    // Clear the input value after selecting a chip for better UX
    setInputValue('');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  return (
    <InputChips
      label="Answer"
      isLabelHidden={true}
      isDisabled={isDisabled || isLoading}
      results={inputChipsItems}
      chips={chips}
      setChips={setChips}
      onChipSelect={handleChipSelect}
      placeholder="Click here or search to select options"
      value={inputValue}
      onChange={handleInputChange}
      className={styles.inputChips}
    />
  );
};
